import { collection, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { db } from '@/firebaseInit';
import { coll_invites, type InviteType } from '@/types/types_invite';
import { coll_partnerUsers, type PartnerUserType } from '@/types/types_partnerUser';

// Read-only data access for invitations and partner accounts.
//
// Unlike partnersRepo.ts there are no write helpers here: both collections are
// written exclusively by Cloud Functions (see src/util/callable_functions.ts),
// which is what keeps an invitation single-use.

const invitesCollection = collection(db, coll_invites);
const partnerUsersCollection = collection(db, coll_partnerUsers);

/**
 * Subscribes to one partner's invitations, newest first. Returns an unsubscribe
 * function, mirroring `subscribePartners`.
 */
export function subscribePartnerInvites(
  partnerId: string,
  onChange: (invites: InviteType[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return onSnapshot(
    query(invitesCollection, where('partnerId', '==', partnerId), orderBy('createdAt', 'desc')),
    (snap) => onChange(snap.docs.map((d) => ({ ...d.data(), id: d.id }) as InviteType)),
    onError,
  );
}

/** Subscribes to the accounts belonging to one partner. */
export function subscribePartnerUsers(
  partnerId: string,
  onChange: (users: PartnerUserType[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return onSnapshot(
    query(partnerUsersCollection, where('partnerId', '==', partnerId)),
    (snap) => onChange(snap.docs.map((d) => ({ ...d.data(), id: d.id }) as PartnerUserType)),
    onError,
  );
}
